import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";

import { Translations, Country, Languages } from "types";
import countriesData from "data/countries.json";

// Country names are static data, so we ship them as a bundled JSON list rather
// than calling an external API at runtime. The previous source (REST Countries
// v3.1) was deprecated and v5 requires an API key with a tight request quota;
// see https://restcountries.com/docs/legacy-api-deprecation
// The list was generated from the v5 API (name + German translation only) and
// can be regenerated with the curl/build steps documented in the PR.
const countries = countriesData as Country[];

export const fetchCountries = (language: string): Country[] => {
  return [...countries].sort((a, b) =>
    getCountryByLanguage(a, language).localeCompare(
      getCountryByLanguage(b, language)
    )
  );
};

// We only display country names in German and English. German UI languages use
// the localized name; everything else falls back to the common (English) name.
export const getCountryByLanguage = (
  country: Country,
  language: string
): string => {
  if (language.startsWith(Languages.german)) {
    return country.translations[Translations.german]?.common ?? country.name.common;
  }

  return country.name.common;
};

export const useCountries = () => {
  const { i18n } = useTranslation();
  const { data: countries, isLoading } = useQuery(["countries", i18n.language], () =>
    fetchCountries(i18n.language)
  );

  return { countries, isLoading, getCountryByLanguage };
};
